"use server";

import type { ProjectMembership } from "@medplum/fhirtypes";
import { z } from "zod";
import { createServerAction } from "@/lib/actions/create-server-action";
import { paginatedSearch } from "@/lib/fhir/bundle";
import { createMedplumBackendClient } from "@/lib/medplum/backend";

const OutputSchema = z.object({ canSwitchProfile: z.boolean() });

/** Whether a project membership should count toward profile switching. */
function isUsableProjectMembership(membership: ProjectMembership): boolean {
  return (
    membership.active !== false &&
    !!membership.user?.reference &&
    !!membership.profile?.reference &&
    !!membership.project?.reference
  );
}

/**
 * Determines whether the authenticated user has more than one usable profile
 * to switch between.
 *
 * Uses the backend client rather than the caller's own session: a patient's
 * scoped AccessPolicy (ENG-4663) does not grant `ProjectMembership` access, so
 * the client-side session can no longer answer "what are my other profiles?"
 * on its own. The lookup stays scoped to the AUTHENTICATED caller's own user
 * reference (resolved from their session profile), never from client input.
 */
export const resolveCanSwitchProfile = createServerAction({
  id: "resolve-can-switch-profile",
  description: "Determines whether the authenticated user has more than one usable profile",
  outputSchema: OutputSchema,
  auth: { required: true },
  handler: async ({ medplum, logger }) => {
    let profileReference: string | undefined;
    let userReference: string | undefined;
    try {
      const profile = await medplum.getProfileAsync();
      if (!profile?.id) {
        return { canSwitchProfile: false };
      }
      profileReference = `${profile.resourceType}/${profile.id}`;

      const medplumBackend = createMedplumBackendClient();
      const currentMembership = await medplumBackend.searchOne("ProjectMembership", {
        profile: profileReference,
      });
      userReference = currentMembership?.user?.reference;
      if (!userReference) {
        return { canSwitchProfile: false };
      }

      let usableCount = 0;
      for await (const membership of paginatedSearch<ProjectMembership>({
        medplum: medplumBackend,
        resourceType: "ProjectMembership",
        query: { user: userReference, _count: "100" },
      })) {
        if (!isUsableProjectMembership(membership)) continue;
        usableCount += 1;
        // Only ever need to distinguish 0/1 vs >1 — stop paging once we know.
        if (usableCount > 1) break;
      }

      return { canSwitchProfile: usableCount > 1 };
    } catch (error) {
      logger.warn("Failed to resolve project memberships for profile switching", {
        error,
        profileReference: profileReference ?? null,
        userReference: userReference ?? null,
      });
      return { canSwitchProfile: false };
    }
  },
});
